import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { ReplyDraftButton } from '@/ai/ReplyDraft'
import { fetchConversations, replyInChat, type Conversation, type MessengerAssignTarget, type SentMessage } from './api'

/**
 * Ответ клиенту в мессенджер с карточки (тикет #125): под лентой, в тот же чат, откуда он писал.
 *
 * Чат выбирается из диалогов, привязанных к карточке; нет ни одного — нет и поля: клиент ещё не
 * писал, и первым написать в Telegram бот не может, а в WhatsApp — только шаблоном.
 *
 * Итог отправки называется словами: «отправлено», «не доставлено — повтор позже» или причина
 * отказа (бот заблокирован, канал не подключён). WhatsApp вне 24 часов с последнего сообщения
 * клиента — поле закрыто заранее и сказано почему, а не после нажатия.
 */
export function MessengerReply({ entityType, recordId }: { entityType: MessengerAssignTarget; recordId: string }) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const mayWrite = can(user, 'sales.activity', 'create')
  const chats = useQuery({
    queryKey: ['messenger-conversations', entityType, recordId],
    queryFn: ({ signal }) => fetchConversations(entityType, recordId, signal),
    enabled: can(user, 'sales.activity', 'read'),
    retry: false,
  })
  const [chosen, setChosen] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [draftTaskId, setDraftTaskId] = useState<string | null>(null)
  const [result, setResult] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null)

  const list = chats.data ?? []
  const chat: Conversation | undefined = list.find((candidate) => candidate.id === chosen) ?? list[0]

  const send = useMutation({
    mutationFn: (value: { conversation: string; text: string; draftTaskId: string | null }) =>
      replyInChat(value.conversation, value.text, crypto.randomUUID(), value.draftTaskId),
    onSuccess: (message: SentMessage) => {
      void queryClient.invalidateQueries({ queryKey: ['timeline', entityType, recordId] })
      void queryClient.invalidateQueries({ queryKey: ['messenger-conversations', entityType, recordId] })
      if (message.status !== 'rejected') setDraftTaskId(null)
      if (message.status === 'sent') {
        setText('')
        setResult({ tone: 'ok', text: t('messenger.reply.sent') })
      } else if (message.status === 'rejected') {
        setResult({ tone: 'error', text: t('messenger.reply.rejected', { reason: message.reason ?? '' }) })
      } else {
        setText('')
        setResult({ tone: 'warn', text: t('messenger.reply.failed') })
      }
    },
    onError: (failure) => setResult({ tone: 'error', text: failure instanceof ApiError ? failure.message : t('app.error') }),
  })

  if (!mayWrite || list.length === 0 || !chat) return null
  const closed = chat.channel === 'whatsapp' && chat.windowOpen === false

  return (
    <form className="panel stack" style={{ gap: 8 }} aria-label={t('messenger.reply.title')} onSubmit={(event) => {
      event.preventDefault()
      if (text.trim() !== '') send.mutate({ conversation: chat.id, text: text.trim(), draftTaskId })
    }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="strong">{t('messenger.reply.title')}</span>
        {list.length > 1 ? (
          <select className="input" aria-label={t('messenger.reply.chat')} value={chat.id}
                  onChange={(event) => { setChosen(event.target.value); setResult(null); setDraftTaskId(null) }}>
            {list.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {t(`messenger.channel.${candidate.channel}`)} · {candidate.title}
              </option>
            ))}
          </select>
        ) : (
          <span className="badge">{t(`messenger.channel.${chat.channel}`)} · {chat.title}</span>
        )}
      </div>
      {!chat.usable && <span className="t-sm" role="note">{t(`messenger.reply.unusable.${chat.channel}`)}</span>}
      {closed ? (
        <span className="t-sm" role="note">
          {t('messenger.reply.windowClosed', {
            at: chat.lastInboundAt ? new Date(chat.lastInboundAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU') : '—',
          })}
        </span>
      ) : (
        <>
          <ReplyDraftButton key={chat.id} channel="messenger" sourceId={chat.id} onDraft={(draft, taskId) => {
            setText(draft)
            setDraftTaskId(taskId)
            setResult(null)
          }} />
          <textarea className="input" rows={3} aria-label={t('messenger.reply.text')} value={text}
                    onChange={(event) => { setText(event.target.value); setResult(null) }} />
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={text.trim() === '' || send.isPending}>
              {send.isPending ? t('messenger.reply.sending') : t('messenger.reply.send')}
            </button>
          </div>
        </>
      )}
      {result && (
        <span className="t-sm" role={result.tone === 'ok' ? 'status' : 'alert'}
              style={result.tone === 'error' ? { color: 'var(--danger-text)' } : undefined}>
          {result.text}
        </span>
      )}
    </form>
  )
}
